import { motion } from "framer-motion";
import { IMAGES } from "../../data/content";
import { Layers, PackageCheck, Wallet } from "lucide-react";

const POINTS = [
  {
    icon: Layers,
    k: "Partitioned Compartments",
    v: "Corrugated dividers and die-cut foam grids split one box into several cavities — each holding a different part."
  },
  {
    icon: PackageCheck,
    k: "Zero Movement, Zero Damage",
    v: "Every part is locked in place with a snug foam fit, eliminating abrasion and impact during multi-modal transit."
  },
  {
    icon: Wallet,
    k: "Fewer Boxes, Lower Cost",
    v: "Consolidating multiple SKUs into a single optimised box cuts packaging, volume and freight spend."
  }
];

export const MultiPart = () => {
  return (
    <section
      data-testid="multipart-section"
      className="relative bg-background py-24 md:py-32 border-t border-border overflow-hidden"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        {/* Text */}
        <div className="order-2 lg:order-1">
          <p className="font-mono-ibm text-xs tracking-widest uppercase text-primary mb-4">
            / One Box, Many Parts
          </p>
          <h2 className="font-display uppercase leading-[0.88] tracking-tight text-5xl md:text-7xl mb-8">
            Multiple Parts,
            <br />
            <span className="text-primary">Single Crate</span>
          </h2>
          <p className="max-w-xl text-base md:text-lg text-muted-foreground leading-relaxed mb-12">
            Need to ship many different components together? We engineer
            multi-cavity foam fitments so several parts travel safely in one
            custom box — perfectly separated, protected and export-ready.
          </p>

          <div className="border-t border-border">
            {POINTS.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.div
                  key={p.k}
                  data-testid={`multipart-point-${i}`}
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.08 }}
                  className="group flex items-start gap-5 py-6 border-b border-border"
                >
                  <span className="w-11 h-11 border border-border flex items-center justify-center shrink-0 group-hover:border-primary group-hover:text-primary transition-colors">
                    <Icon size={18} />
                  </span>
                  <div>
                    <h3 className="font-display uppercase tracking-tight text-xl md:text-2xl">
                      {p.k}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1 max-w-md leading-relaxed">
                      {p.v}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Image */}
        <motion.div
          initial={{ opacity: 0, scale: 1.05 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: [0.76, 0, 0.24, 1] }}
          className="order-1 lg:order-2 group relative aspect-[4/3] overflow-hidden border border-border"
        >
          <img
            src={IMAGES.multiCompartment}
            alt="Corrugated multi-compartment boxes partitioned to hold several parts in one crate"
            className="w-full h-full object-cover img-industrial"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background/60 to-transparent" />
          <span className="absolute top-5 left-5 font-mono-ibm text-[10px] md:text-xs tracking-widest text-foreground bg-background/60 backdrop-blur-sm px-3 py-2">
            [ MULTI-COMPARTMENT CRATES ]
          </span>
        </motion.div>
      </div>
    </section>
  );
};
